import React from 'react';
import { cn } from '../../lib/utils';

/**
 * CompassHUD Component
 * 
 * Persistent progress indicator that shows user's position in the scrollytelling journey.
 * Fixed to bottom-right corner with a circular progress ring.
 * 
 * @param {Object} props
 * @param {number} props.currentScene - Active scene index (0-5)
 * @param {Array<string>} props.sceneNames - Names of all scenes
 * @param {number} props.progress - Overall progress percentage (0-100)
 * @returns {JSX.Element}
 */
export const CompassHUD = ({
  currentScene = 0,
  sceneNames = ['Orientation', 'Leak', 'Fix', 'Flow', 'Future', 'Decision'],
  progress = 0,
}) => {
  const circumference = 2 * Math.PI * 20; // radius = 20
  const strokeDashoffset = circumference - (progress / 100) * circumference;

  return (
    <>
      {/* Progress Bar at top */}
      <div
        className="fixed top-0 left-0 right-0 z-50 h-1 bg-white/10"
        data-testid="progress-bar-container"
      >
        <div
          className="h-full bg-gradient-to-r from-teal-400 to-teal-300 transition-all duration-300 ease-out shadow-[0_0_10px_rgba(17,197,217,0.5)]"
          style={{ width: `${progress}%` }}
          data-testid="progress-bar"
        />
      </div>

      {/* Compass HUD (existing) */}
      <div
        className={cn(
          'fixed',
          'right-3 bottom-3 sm:right-6 sm:bottom-6',
          'z-40',
          'pointer-events-none'
        )}
        data-testid="compass-hud"
      >
      {/* Circular Progress Ring */}
      <div
        className={cn(
          'relative',
          'size-12 sm:size-14',
          'rounded-full',
          'bg-white/5',
          'border border-white/15',
          'backdrop-blur-md',
          'shadow-[0_4px_18px_rgba(0,0,0,0.35)]',
          'flex items-center justify-center'
        )}
        data-testid="compass-hud-ring"
      >
        {/* SVG Progress Circle */}
        <svg
          className="absolute inset-0 -rotate-90"
          width="100%"
          height="100%"
          viewBox="0 0 48 48"
        >
          <circle
            cx="24"
            cy="24"
            r="20"
            fill="none"
            stroke="rgba(255,255,255,0.1)"
            strokeWidth="2"
          />
          <circle
            cx="24"
            cy="24"
            r="20"
            fill="none"
            stroke="url(#progressGradient)"
            strokeWidth="2"
            strokeDasharray={circumference}
            strokeDashoffset={strokeDashoffset}
            strokeLinecap="round"
            className="transition-all duration-300 ease-out"
            data-testid="compass-progress"
          />
          <defs>
            <linearGradient id="progressGradient" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="hsl(187 77% 67%)" stopOpacity="0.5" />
              <stop offset="100%" stopColor="hsl(34 100% 75%)" stopOpacity="0.4" />
            </linearGradient>
          </defs>
        </svg>

        {/* Center Compass Icon */}
        <div className="relative z-10 text-white/80 text-xs font-mono font-semibold">
          {currentScene + 1}
        </div>
      </div>

      {/* Scene Label (appears on hover - desktop only) */}
      <div
        className={cn(
          'absolute',
          'right-full mr-3',
          'top-1/2 -translate-y-1/2',
          'px-3 py-1.5',
          'rounded-md',
          'bg-white/10 backdrop-blur-xl',
          'border border-white/15',
          'opacity-0 hover:opacity-100',
          'transition-opacity duration-200',
          'pointer-events-none',
          'whitespace-nowrap',
          'hidden sm:block'
        )}
        data-testid="compass-label"
      >
        <span className="text-xs font-sans text-white/90">
          {sceneNames[currentScene]}
        </span>
        <span className="ml-2 text-xs font-mono text-white/60">
          {Math.round(progress)}%
        </span>
      </div>
    </div>
    </>
  );
};